import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage, { friendlyErrorMessage } from '../components/ErrorMessage';
import ApprovalStatus from '../components/ApprovalStatus';
import OrderActivityHistory from '../components/OrderActivityHistory';
import OrderPdfButton from '../components/OrderPdfButton';
import OrderForm from '../components/OrderForm';
import { formatCurrency } from '../utils/formatCurrency';
import { apiFetch, apiGet, apiPost, apiPatch, apiPut, API_BASE } from '../utils/api';
import { makeReceivedItemHandlers } from '../utils/receivedItems';
import { lineStepMap } from '../components/OrderItemReceivedControls';
import { countDelivery } from '../utils/deliveryCountableItems';
import { groupItemAttachmentsByItemId, describePendingClaimFailures } from '../utils/orderFileMutations';
import usePendingOrderUploads from '../hooks/usePendingOrderUploads';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import { useLatestRequest } from '../hooks/useSearchRequest';
import remountOnRouteParams from '../utils/remountOnRouteParams';
import { notify as notifyDataBus } from '../utils/dataBus';
import { useDepartment } from '../contexts/DepartmentContext';
import { useAuth } from '../contexts/AuthContext';
import { useOrderAuthority } from '../hooks/useOrderAuthority';
import { useVatMode } from '../hooks/useVatMode';
import { STATUS_COLORS, STATUS_LABELS } from '../constants/colors';
import { DELIVERY_STAGE_STATUSES, RECEIVE_ENABLED_STATUSES, RECEIVING_LOCKED_TOOLTIP, showsTimeCriticalChrome, managerMayCancelOrder, requesterMayCancelOrder } from '../constants/statuses';
import { formatReadableDate, toLocalISO } from '../utils/dateFormat';
import { getOrderTitle } from '../utils/orderTitle';
import { deliveredTickNotice } from '../utils/deliveredTickNotice';
import { BTN, BTN_XS } from '../constants/buttons';
import InfoTip from '../components/InfoTip';
import { INTERNAL_NOTES_LABEL, INTERNAL_NOTES_TOOLTIP, INTERNAL_NOTES_PLACEHOLDER } from '../constants/orderNotes';
import { FIELD_LABEL_CLASS } from '../constants/inputs';

// 🔀 2026-09-13 — the status set and its phrases moved to the shared deck the
// post-approval pause reads; this page's hand-rolled "Confirm Edit" modal is
// replaced by that one dialog (owner: *"seems like we should be using common
// popup style for editing orders etc?"*). Aliased so the blue in-form note
// below is untouched.
import PostApprovalEditDialog from '../components/PostApprovalEditDialog';
import OrderRejectionLine from '../components/OrderRejectionLine';
import {
  POST_APPROVAL_STATUSES,
  POST_APPROVAL_STATUS_PHRASE as POST_APPROVAL_LABELS,
  buildEditReason,
} from '../constants/postApprovalEdit';

/**
 * Unified order page used by both `/orders/:id` (view) and `/orders/:id/edit`
 * (edit). Mounts OrderForm with `readOnly` toggled by local `mode` state so
 * the page no longer remounts on Edit / Save / Cancel — fields just swap
 * between plain text and inputs in place.
 *
 * View-mode chrome (status pills, approval workflow, delivery progress, admin
 * actions, attachments, audit log) renders regardless of mode.
 */
// View mode tracks no per-line in-flight set (its uploads refetch the page).
const NO_ITEM_UPLOADS = new Set();

function OrderPage({ initialMode = 'view' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { formatMoney } = useVatMode();
  const { isAdmin: contextIsAdmin, capabilities, capabilitiesFor, activeDepartment, assignments, loading: deptLoading } = useDepartment();
  const { user } = useAuth();
  const { canManageOrder } = useOrderAuthority();
  // The cost-code / payment-type feeds answer [] to a member without
  // canViewBudget IN THIS DEPARTMENT (routes/budget.js, both /active routes);
  // mirror that, never the unscoped buyer flag (LR, 2026-09-24).
  const canLoadAccountingPickers = contextIsAdmin || capabilities.canViewBudget === true;

  // ---------------- Data state ----------------
  const [order, setOrder] = useState(null);
  // EDIT-MODE ATTACHMENTS (2026-09-15, R13): uploaded AT PICK to the caller's
  // pending store and CLAIMED by `submitEdit` after its PUT; Cancel and leaving
  // the page discard them (owner: *"upload the file straight away, delete it if
  // the user does not save the line or they delete the line"*). Replaces the
  // 2026-09-14 staged-in-the-browser flush and its progress trackers.
  const pending = usePendingOrderUploads();
  const [productions, setProductions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  // Supplier names typed on PAST APPROVED ORDERS — the SECOND tier of the
  // supplier picker, never merged into `suppliers` (see utils/supplierOptions.js).
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [gags, setGags] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  // Which of the reference lists above are empty because their request FAILED,
  // rather than because the department has none. `OrderForm` renders confident
  // sentences off these arrays and cannot see the requests behind them — see
  // the `loadFailures` prop in `components/OrderForm.js`.
  const [loadFailures, setLoadFailures] = useState({});
  const [supplierContact, setSupplierContact] = useState(null);
  // The audit log itself now lives in `components/OrderActivityHistory.js`,
  // which owns the fetch, the loading state and the collapse preference. What
  // remains here is a COUNTER this page bumps after anything that writes an
  // audit row, so the section reloads if it is open.
  const [auditRefreshSignal, setAuditRefreshSignal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ---------------- Mode + form save state ----------------
  const [mode, setMode] = useState(initialMode === 'edit' ? 'edit' : 'view');
  const [editPermissionDenied, setEditPermissionDenied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [pendingPayload, setPendingPayload] = useState(null);
  const showEditWarning = pendingPayload !== null;
  // Track dirtiness so we can guard navigation when in edit mode.
  // ⚠️ `dirty` is set only by a pending-delete toggle and by a Save attempt —
  // it NEVER saw a typed edit, so the `beforeunload` guard it fed never fired
  // for one (found R14, 2026-09-15). `formDirty` is `OrderForm`'s own snapshot
  // predicate (`onDirtyChange`), the one the inline editor already uses.
  const [dirty, setDirty] = useState(false);
  const [formDirty, setFormDirty] = useState(false);

  // ---------------- View-mode action state ----------------
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectError, setRejectError] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const [collapsedDetailItems, setCollapsedDetailItems] = useState(new Set());
  const [showSupplierContact, setShowSupplierContact] = useState(false);
  const [showOrderedForm, setShowOrderedForm] = useState(false);
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [editingExpectedDate, setEditingExpectedDate] = useState(false);
  const [editExpectedDate, setEditExpectedDate] = useState('');
  const [expectedDateSaving, setExpectedDateSaving] = useState(false);
  const [deliveryUploading, setDeliveryUploading] = useState(false);
  const [showCancelForm, setShowCancelForm] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancelError, setCancelError] = useState('');
  const deliveryFileInputRef = useRef(null);

  // ---------------- Attachment management (shared view + edit) ----------------
  // New files: VIEW mode uploads them straight onto the order (below); EDIT mode
  // uploads them to the pending store at pick (`pending`, above) and claims on Save.
  // Pending-delete: file ids whose DELETE should fire on Save (edit mode only).
  const [pendingDeleteFileIds, setPendingDeleteFileIds] = useState(new Set());

  const isUploadMode = new URLSearchParams(location.search).get('upload') === '1';

  // ---------------- Load order + companion lists ----------------
  const fetchOrder = useCallback(() => {
    setLoading(true);
    setError('');
    apiGet(`${API_BASE}/api/orders/${id}`)
      .then(res => {
        if (!res.ok) throw new Error('Order not found');
        return res.json();
      })
      .then(data => {
        setOrder(data);
        setAdminNotes(data.admin_notes || '');
        setLoading(false);
        // A genuinely fresh order answers whatever `receivingStale` was
        // guarding against (see the W1 close-out block below) — clear it so
        // a page that recovers by some OTHER path (e.g. an approve/reject
        // action re-running `fetchOrder`) also un-sticks the received ticks,
        // not only an explicit reload.
        setReceivingStale(false);
        if (data.supplier_name && data.department_id) {
          apiGet(`${API_BASE}/api/suppliers/by-name/${encodeURIComponent(data.supplier_name)}?departmentId=${data.department_id}`)
            .then(r => r.ok ? r.json() : null)
            .then(supplier => { if (supplier) setSupplierContact(supplier); })
            .catch(() => {});
        }
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [id]);

  /**
   * Ask the Activity History section to reload.
   *
   * 🔑 THE NAME IS KEPT DELIBERATELY. Ten call sites across this page invoke
   * `fetchAuditLog()` after an action that writes an `order_audit_log` row
   * (approve, reject, mark ordered, mark delivered, cancel, uncancel, notes,
   * expected-delivery, item-received, edit). Keeping the identifier means the
   * extraction touched none of them, so this is provably a change of MECHANISM
   * and not of WHEN the log refreshes — renaming it would have put ten
   * unreviewable edits in a diff that is otherwise about moving a component.
   *
   * It no longer fetches: `components/OrderActivityHistory.js` owns the request
   * and reloads when this counter changes, but only if the section has actually
   * been opened. So an action performed while the history is collapsed now
   * costs NOTHING, where it used to issue a full audit-log fetch nobody saw.
   */
  const fetchAuditLog = useCallback(() => {
    setAuditRefreshSignal(n => n + 1);
  }, []);

  // Load order on mount / id change. The audit log is no longer fetched here —
  // the history section fetches itself, lazily, on first open.
  useEffect(() => {
    fetchOrder();
    apiPost(`${API_BASE}/api/notifications/admin/mark-read`, { order_id: parseInt(id) }).catch(() => {});
  }, [id]);

  // Load productions / suppliers / sub-departments once we know we'll need them
  // (always, since OrderForm needs the dropdowns even in read-only to look up
  // labels). Cost codes + payment types only for admin/buyer.
  useEffect(() => {
    Promise.all([
      apiGet(`${API_BASE}/api/productions`).then(r => r.ok ? r.json() : []),
      // 🪤 `null` IS THE FAILURE SENTINEL FOR THE TWO SUPPLIER CALLS, AND A
      // NON-OK RESPONSE REACHES IT BY THE SAME ROUTE A REJECTION DOES —
      // `r.ok ? … : null` covers the refusal, `.catch(() => null)` covers the
      // rejection and a malformed body. `[]` cannot serve: it is exactly what a
      // department with no supplier records legitimately returns, and telling
      // those two apart is the entire point (see `loadFailures` above).
      apiGet(`${API_BASE}/api/suppliers`).then(r => (r.ok ? r.json() : null)).catch(() => null),
      apiGet(`${API_BASE}/api/sub-departments`).then(r => r.ok ? r.json() : []),
      // Gag names for the Job / Rig / Gag autocomplete (edit mode). Optional —
      // on failure the field stays free-text. Genuinely optional, unlike the
      // supplier calls: `OrderForm`'s gag hint makes no claim that invites a
      // damaging action, so it is deliberately not tracked here.
      apiGet(`${API_BASE}/api/gags`).then(r => r.ok ? r.json() : { gags: [] }),
      // The second supplier tier. Reported under the SAME `suppliers` key as
      // the records: the two are merged into one picker
      // (`utils/supplierOptions.js`), so "half the list is missing" is the only
      // distinction the person typing could act on.
      apiGet(`${API_BASE}/api/suppliers/name-suggestions`).then(r => (r.ok ? r.json() : null)).catch(() => null),
    ])
      .then(([prods, supps, subDepts, gagResp, supplierNames]) => {
        setProductions(prods);
        setSuppliers(Array.isArray(supps) ? supps : []);
        setSubDepartments(subDepts);
        setGags(Array.isArray(gagResp?.gags) ? gagResp.gags : []);
        setSupplierSuggestions(Array.isArray(supplierNames) ? supplierNames : []);
        setLoadFailures(prev => ({ ...prev, suppliers: supps === null || supplierNames === null }));
      })
      // Reached when the `.then` above throws, or if a future entry drops its
      // own catch. Assumes the worst about the picker rather than saying nothing.
      .catch(() => setLoadFailures(prev => ({ ...prev, suppliers: true })));
  }, []);

  // 🔑 STALE-RESPONSE GUARDS on the two department-scoped option lists (R14
  // review M2). The notification bell switches department only once its
  // navigation to this page has LANDED (`components/Layout.js`), so this page
  // mounts under the OLD department and refetches for the new one a moment
  // later. Without a sequence the old department's list can resolve second and
  // win — a cost-code select offering another department's codes.
  const beginCostCodes = useLatestRequest();
  const beginPaymentTypes = useLatestRequest();

  useEffect(() => {
    const isCurrent = beginCostCodes();
    if (deptLoading || !activeDepartment || !canLoadAccountingPickers) {
      setCostCodes([]);
      // Clears the flag too: this branch empties the list on purpose, and a
      // stale failure would print "couldn't be loaded" over a list nobody asked for.
      setLoadFailures(prev => ({ ...prev, costCodes: false }));
      return;
    }
    // An emptied option list silently redraws every coded line as `-- None --`
    // (a controlled `<select>` whose value has no matching `<option>` falls
    // back to `selectedIndex = 0`), and a manager correcting the apparent gap
    // can only pick `-- None --`, which the PATCH writes as `null` over a real
    // cost code. So the failure has to reach the form. Non-ok throws, so it
    // takes the same path as a rejection.
    apiGet(`${API_BASE}/api/budget/cost-codes/active?department_id=${activeDepartment.department_id}`)
      .then(r => { if (!r.ok) throw new Error('Failed to load cost codes'); return r.json(); })
      .then(data => {
        if (!isCurrent()) return;
        setCostCodes(data);
        setLoadFailures(prev => ({ ...prev, costCodes: false }));
      })
      .catch(() => {
        if (!isCurrent()) return;
        setCostCodes([]);
        setLoadFailures(prev => ({ ...prev, costCodes: true }));
      });
  }, [activeDepartment, deptLoading, canLoadAccountingPickers, beginCostCodes]);

  useEffect(() => {
    const isCurrent = beginPaymentTypes();
    if (deptLoading || !activeDepartment || !canLoadAccountingPickers) {
      setPaymentTypes([]);
      setLoadFailures(prev => ({ ...prev, paymentTypes: false }));
      return;
    }
    // Same shape as cost codes above — read that block. Tracked separately
    // because these are two requests and either can be refused alone.
    apiGet(`${API_BASE}/api/budget/payment-types/active?department_id=${activeDepartment.department_id}`)
      .then(r => { if (!r.ok) throw new Error('Failed to load payment types'); return r.json(); })
      .then(data => {
        if (!isCurrent()) return;
        setPaymentTypes(data);
        setLoadFailures(prev => ({ ...prev, paymentTypes: false }));
      })
      .catch(() => {
        if (!isCurrent()) return;
        setPaymentTypes([]);
        setLoadFailures(prev => ({ ...prev, paymentTypes: true }));
      });
  }, [activeDepartment, deptLoading, canLoadAccountingPickers, beginPaymentTypes]);

  // If we mounted in edit mode but the server says we can't edit, flag it and
  // fall back to view mode rather than redirecting away — the user can still
  // read the order.
  useEffect(() => {
    if (!order) return;
    if (initialMode === 'edit' && order.can_edit === false) {
      setEditPermissionDenied(true);
      setMode('view');
    }
  }, [order, initialMode]);

  // Upload deep-link side effect (lifted from the old OrderDetail page, which
  // this component replaced and which no longer exists).
  useEffect(() => {
    if (isUploadMode && order && !loading) {
      const t = setTimeout(() => {
        const el = document.querySelector('[data-testid="delivery-paperwork-section"]');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-blue-400', 'ring-offset-2', 'rounded-lg');
          setTimeout(() => el.classList.remove('ring-2', 'ring-blue-400', 'ring-offset-2', 'rounded-lg'), 3000);
        }
      }, 300);
      return () => clearTimeout(t);
    }
  }, [isUploadMode, order, loading]);

  // Leaving edit mode's unsaved work (a pending upload counts) — every in-app
  // navigation, Back, reload and tab close. Was a bare `beforeunload` effect
  // that could not see a sidebar click (R14, 2026-09-15; see the hook).
  // Save and Cancel never navigate (both `setMode('view')` on this URL), so
  // no post-save bypass is needed.
  const hasPendingUploads = pending.hasEntries;
  const leaveGuard = useUnsavedChangesGuard({
    when: mode === 'edit' && (formDirty || dirty || hasPendingUploads),
    message: `Your changes to this order haven't been saved. If you leave, they${hasPendingUploads ? ' and any attached files' : ''} will be lost.`,
  });

  // ------- View-mode action handlers (lifted from the old OrderDetail page) -------
  const toggleDetailItemCollapse = (index) => {
    setCollapsedDetailItems(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index); else next.add(index);
      return next;
    });
  };

  const handleApprove = async () => {
    if (!window.confirm('Are you sure you want to approve this order?')) return;
    setActionLoading(true);
    setActionMessage(null);
    try {
      const res = await apiPatch(`${API_BASE}/api/orders/${id}/status`, { status: 'approved' });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to approve order');
      }
      const updated = await res.json();
      setOrder(prev => ({ ...prev, ...updated }));
      setActionMessage({ type: 'success', text: 'Order approved successfully!' });
      fetchAuditLog();
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkOrdered = async () => {
    setActionLoading(true);
    setActionMessage(null);
    try {
      const res = await apiPatch(`${API_BASE}/api/orders/${id}/status`, {
        status: 'ordered',
        expected_delivery_date: expectedDeliveryDate || null,
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to mark order as ordered');
      }
      const updated = await res.json();
      setOrder(prev => ({ ...prev, ...updated }));
      setActionMessage({ type: 'success', text: 'Order marked as ordered successfully!' });
      setShowOrderedForm(false);
      setExpectedDeliveryDate('');
      fetchAuditLog();
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateExpectedDate = async () => {
    setExpectedDateSaving(true);
    try {
      const res = await apiPatch(`${API_BASE}/api/orders/${id}/expected-delivery`, { expected_delivery_date: editExpectedDate || null });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to update expected delivery date');
      }
      const updated = await res.json();
      setOrder(prev => ({ ...prev, ...updated }));
      setEditingExpectedDate(false);
      setActionMessage({ type: 'success', text: 'Expected delivery date updated!' });
      fetchAuditLog();
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setExpectedDateSaving(false);
    }
  };

  const handleMarkDelivered = async () => {
    // 🔑 THE DELIVERED WARNING (2026-09-12). This route now ticks every
    // not-already-received item on the way to `delivered` (owner ruling; see
    // `utils/deliveredTickNotice.js`), so the confirm has to say so. THIS PAGE
    // IS A REAL DOOR TO THAT WRITE even though it is on the retirement
    // inventory: it sends no `reset_side_state`, and the server rule is keyed
    // on the TARGET STATUS, not on that flag.
    if (!window.confirm(`Mark this order as delivered/arrived?\n\n${deliveredTickNotice(order)}`)) return;
    setActionLoading(true);
    setActionMessage(null);
    try {
      const res = await apiPatch(`${API_BASE}/api/orders/${id}/status`, { status: 'delivered' });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to mark order as delivered');
      }
      const updated = await res.json();
      setOrder(prev => ({ ...prev, ...updated }));
      setActionMessage({ type: 'success', text: 'Order marked as delivered successfully!' });
      fetchAuditLog();
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      setRejectError('A rejection reason is required');
      return;
    }
    if (!window.confirm('Are you sure you want to reject this order?')) return;
    setActionLoading(true);
    setActionMessage(null);
    setRejectError('');
    try {
      const res = await apiPatch(`${API_BASE}/api/orders/${id}/status`, { status: 'rejected', rejection_reason: rejectionReason.trim() });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to reject order');
      }
      const updated = await res.json();
      setOrder(prev => ({ ...prev, ...updated, status: 'rejected', rejection_reason: rejectionReason.trim() }));
      setActionMessage({ type: 'success', text: 'Order rejected successfully.' });
      fetchAuditLog();
      setShowRejectForm(false);
      setRejectionReason('');
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    setActionLoading(true);
    setActionMessage(null);
    setCancelError('');
    try {
      const res = await apiPatch(`${API_BASE}/api/orders/${id}/cancel`, { cancellation_reason: cancellationReason.trim() || null });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to cancel order');
      }
      const updated = await res.json();
      setOrder(prev => ({ ...prev, ...updated }));
      setActionMessage({ type: 'success', text: 'Order cancelled successfully.' });
      // Cancel removes the order from Cost to Date (status='cancelled' is
      // excluded by /api/budget/report) — important to broadcast.
      notifyDataBus('budget-changed', { departmentId: order?.department_id, reason: 'cancel' });
      setShowCancelForm(false);
      setCancellationReason('');
      fetchAuditLog();
    } catch (err) {
      if (err.message.includes('already been ordered')) {
        setCancelError(err.message);
      } else {
        setActionMessage({ type: 'error', text: err.message });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveAdminNotes = async () => {
    setNotesSaving(true);
    setActionMessage(null);
    try {
      const res = await apiPost(`${API_BASE}/api/orders/${id}/notes`, { admin_notes: adminNotes });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to save internal notes');
      }
      const updated = await res.json();
      setOrder(prev => ({ ...prev, ...updated }));
      setActionMessage({ type: 'success', text: 'Internal notes saved successfully!' });
      fetchAuditLog();
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setNotesSaving(false);
    }
  };

  // ── RECEIVED TICKS — routed through the shared cluster (2026-08-19) ──────
  // This page carried its OWN per-item received checkbox + PATCH call,
  // predating the 2026-08-15 received-tick cluster (`OrderItemReceivedControls.js`
  // + `utils/receivedItems.js`) that `DepartmentOrders.js`/`MyOrders.js` already
  // use. That bespoke checkbox never picked up the 2026-08-16 "greyed until
  // Ordered" gate, so it stayed live-looking on `approved` and 400'd
  // (`order_not_ordered`) — see CLAUDE.md. Fixed by deleting it and passing the
  // SAME `received` prop shape into `<OrderForm>` below that DepartmentOrders'
  // `OrderExpandedRow` builds — `OrderForm` already supports it (built for this
  // page originally). `makeReceivedItemHandlers` is the ONE mutation path
  // (per-item PATCH, bulk receive-all); this page has no separate list row to
  // refresh, so only `onDetailRefreshed` is wired.
  const [receivingBusy, setReceivingBusy] = useState(false);
  // 🔴 W1 close-out (2026-08-27) — `utils/receivedItems.js` calls
  // `onDetailInvalidated` when a tick/receive-all PATCH SUCCEEDS but the
  // follow-up `GET /api/orders/:id` refresh does not.
  //
  // 🚨 **THIS PAGE IS NOT DepartmentOrders.js/MyOrders.js AND DOES NOT GET
  // THEIR FIX.** Those pages key a `{ orderId: detail }` CACHE — deleting one
  // entry leaves everything else on screen untouched, and `toggleExpand`'s
  // fetch-when-empty guard repairs it on the next re-expand. This page holds
  // exactly ONE order in `order` state and has no expand/collapse at all:
  //   - Clearing `order` the way a cache entry is deleted would blank the
  //     WHOLE PAGE (`if (!order) return null;` below) — far worse than the
  //     stale tick this closes, for an order that was just proven to still
  //     exist and whose mutation just succeeded.
  //   - Re-running `fetchOrder()` sets `loading = true`, which hits the
  //     early `if (loading) return <spinner>;` render gate and UNMOUNTS
  //     `OrderForm` — including any in-progress, unsaved edit in `mode ===
  //     'edit'`. Auto-retrying behind the user's back is not safe here.
  // So instead of repairing the cache, this LOCKS the received controls —
  // reusing the SAME `locked`/`lockedReason` mechanism, which `LineOrderedDeliveredControls`
  // and `NextStepAllButton` read as `disabled={busy || locked}` (2026-10-28).
  // Nothing is left pressable, no other page state is touched, and a
  // genuinely fresh order (`fetchOrder` succeeding by ANY path — a reload,
  // or another action on this page) clears it again, above.
  const [receivingStale, setReceivingStale] = useState(false);
  const receivedHandlers = useMemo(() => makeReceivedItemHandlers({
    onDetailRefreshed: (orderId, freshDetail) => {
      setOrder(freshDetail);
      // ⚖️ GOODS ONLY (owner ruling 2026-09-19) — this sentence is read by a
      // person straight after they ticked something, so counting a credit note
      // in it is the same lie the status used to tell, in a toast.
      const { received: newReceived, total: newTotal } = countDelivery(freshDetail.items);
      setActionMessage({ type: 'success', text: `${newReceived} of ${newTotal} items received` });
    },
    onDetailInvalidated: () => setReceivingStale(true),
    onBusyChange: (orderId, busy) => { setReceivingBusy(busy); if (busy) setActionMessage(null); },
    onError: (err) => setActionMessage({ type: 'error', text: err.message }),
  }), []);

  const handleDeliveryFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setDeliveryUploading(true);
    setActionMessage(null);
    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));
      const res = await apiFetch(`${API_BASE}/api/orders/${id}/files?category=delivery`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to upload delivery paperwork');
      }
      const data = await res.json();
      const orderRes = await apiGet(`${API_BASE}/api/orders/${id}`);
      if (orderRes.ok) {
        const updatedOrder = await orderRes.json();
        setOrder(updatedOrder);
      }
      setActionMessage({ type: 'success', text: `${data.files.length} delivery paperwork file(s) uploaded successfully` });
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    } finally {
      setDeliveryUploading(false);
      if (deliveryFileInputRef.current) deliveryFileInputRef.current.value = '';
    }
  };

  // ---------------- Attachment handlers ----------------
  // VIEW mode only: a pick POSTs immediately and refetches. (Edit mode's picks go
  // to `pending` through OrderForm's `pendingFiles` — see the render.)
  const uploadOrderFilesNow = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    setActionMessage(null);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      const res = await apiFetch(`${API_BASE}/api/orders/${id}/files`, { method: 'POST', body: formData });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to upload attachment(s)');
      }
      const data = await res.json();
      const refetch = await apiGet(`${API_BASE}/api/orders/${id}`);
      if (refetch.ok) {
        const updated = await refetch.json();
        setOrder(updated);
      }
      setActionMessage({ type: 'success', text: `${(data.files || []).length} attachment(s) uploaded` });
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    }
  }, [id]);

  const uploadItemFilesNow = useCallback(async (itemId, files) => {
    if (!itemId || !files || files.length === 0) return;
    setActionMessage(null);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      const res = await apiFetch(`${API_BASE}/api/orders/${id}/items/${itemId}/files`, { method: 'POST', body: formData });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to upload item attachment(s)');
      }
      const refetch = await apiGet(`${API_BASE}/api/orders/${id}`);
      if (refetch.ok) {
        const updated = await refetch.json();
        setOrder(updated);
      }
      setActionMessage({ type: 'success', text: `${files.length} attachment(s) uploaded to item` });
    } catch (err) {
      setActionMessage({ type: 'error', text: err.message });
    }
  }, [id]);

  const handleExistingFileToggleRemove = async (fileId) => {
    if (mode === 'view') {
      if (!window.confirm('Remove this attachment? This cannot be undone.')) return;
      setActionMessage(null);
      try {
        const res = await apiFetch(`${API_BASE}/api/orders/${id}/files/${fileId}`, { method: 'DELETE' });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.error || 'Failed to remove attachment');
        }
        const refetch = await apiGet(`${API_BASE}/api/orders/${id}`);
        if (refetch.ok) {
          const updated = await refetch.json();
          setOrder(updated);
        }
        setActionMessage({ type: 'success', text: 'Attachment removed' });
      } catch (err) {
        setActionMessage({ type: 'error', text: err.message });
      }
    } else {
      setPendingDeleteFileIds(prev => {
        const nextSet = new Set(prev);
        if (nextSet.has(fileId)) nextSet.delete(fileId); else nextSet.add(fileId);
        return nextSet;
      });
      setDirty(true);
    }
  };

  // ---------------- Edit-mode save ----------------
  // `editReason` (2026-09-13) is sent only when non-blank, so a save without
  // one writes an audit row byte-identical to before the reason box existed.
  // `claimLines` — `usePendingOrderUploads#planClaims`'s lines for this save,
  // planned BEFORE the PUT (a refused plan never reaches the PUT).
  const submitEdit = async (payload, editReason = '', claimLines = []) => {
    setSubmitError('');
    setSubmitting(true);
    try {
      const body = editReason ? { ...payload, edit_reason: editReason } : payload;
      const res = await apiPut(`${API_BASE}/api/orders/${id}`, body);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update order');
      }
      const putResponse = await res.json();

      // Claim the pending uploads onto the saved order, then run pending deletes.
      // 🚨 PER-ITEM TARGETS RESOLVE BY THE LINE, NEVER BY POSITION (2026-09-14):
      // a blank-description line is dropped by `buildPayload` before the PUT, so
      // `putResponse.items[formIndex]` named the NEXT line. A new line's claim
      // takes the id the server echoed its `client_key` onto; a line that cannot
      // be identified is REPORTED, never guessed (`claimPlanned`).
      const claimFailures = claimLines.length > 0
        ? await pending.claimPlanned(id, claimLines, putResponse.items || [])
        : [];

      for (const fileId of pendingDeleteFileIds) {
        const delRes = await apiFetch(`${API_BASE}/api/orders/${id}/files/${fileId}`, { method: 'DELETE' });
        if (!delRes.ok) {
          const err = await delRes.json();
          throw new Error(err.error || 'Order saved but attachment removal failed');
        }
      }

      setPendingDeleteFileIds(new Set());

      if (claimFailures.length > 0) {
        // 🚨 THE ORDER IS SAVED; SOME FILES ARE NOT ATTACHED. Stay in edit mode
        // with the files still listed, re-read the SAVED order so the new lines
        // carry their ids (a second Save must not insert them again) and Save
        // again retries the claims.
        setSubmitError(describePendingClaimFailures(claimFailures));
        setDirty(false);
        fetchOrder();
        fetchAuditLog();
        return;
      }

      setSubmitSuccess(true);
      setDirty(false);
      // Line-item edits change unit_price/quantity/cost_code → Cost to Date
      // and per-cost-code totals shift. Tell other tabs. Hire line items
      // can also be added / removed / re-dated by the same save, so the
      // Hires list needs to know too.
      notifyDataBus('budget-changed', { departmentId: order?.department_id, reason: 'edit' });
      notifyDataBus('hires-changed', { departmentId: order?.department_id, reason: 'order-edit' });
      // Refresh order, flip back to view mode (no redirect — same page).
      fetchOrder();
      fetchAuditLog();
      // Flip back to view mode quickly so inputs lock, but keep the
      // success pill visible for a beat so the user notices the save landed.
      setTimeout(() => setMode('view'), 400);
      setTimeout(() => setSubmitSuccess(false), 2500);
    } catch (err) {
      setSubmitError(friendlyErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  // 🔑 ONE PAUSE PER INTENT, the inline editor's rule (`OrderInlineEditor.js`
  // `handleSubmit`): a save whose only change is a removal already confirmed
  // in `OrderForm`'s removal pause skips this one. This page adds one term the
  // row does not need — PENDING UPLOADS and pending deletes are edits
  // `OrderForm`'s snapshot cannot see, so either brings the pause back.
  const handleSaveFromForm = async (payload, meta) => {
    const formItems = (meta && meta.items) || null;
    const planned = pending.planClaims(formItems, payload && payload.items);
    if (planned.message) {
      setSubmitError(planned.message);
      return;
    }
    setDirty(true);
    const removals = meta && meta.postApprovalRemovals;
    const removalNotes = (removals && removals.notes) || [];
    const hasFileChanges = pending.hasEntries || pendingDeleteFileIds.size > 0;
    const onlyConfirmedRemovals = !!(removals && removals.onlyChange) && !hasFileChanges;
    if (POST_APPROVAL_STATUSES.includes(order?.status) && !onlyConfirmedRemovals) {
      setPendingPayload({ payload, removalNotes, claimLines: planned.lines });
      return;
    }
    await submitEdit(payload, buildEditReason('', removalNotes), planned.lines);
  };

  const confirmPostApprovalEdit = (reason) => {
    const p = pendingPayload;
    setPendingPayload(null);
    if (p) submitEdit(p.payload, buildEditReason(reason, p.removalNotes), p.claimLines);
  };

  const handleCancelEdit = () => {
    const hasFileChanges = pending.hasEntries || pendingDeleteFileIds.size > 0;
    if ((dirty || hasFileChanges) && !window.confirm('Discard unsaved changes?')) return;
    setDirty(false);
    setSubmitError('');
    pending.discardAll(); // not saved → deleted on the server
    setPendingDeleteFileIds(new Set());
    setMode('view');
  };

  // ---------------- Render gates ----------------
  // 🔑 EVERY RETURN BELOW IS `<>{leaveGuard.dialog}<div …/></>` — THE DIALOG AT THE
  // SAME TREE POSITION IN EACH (R14 review M-d). A save whose file claims failed
  // refetches (the spinner) with those files still pending, so the guard stays
  // armed across it; if one branch wraps the dialog in a `<div>` and another in a
  // Fragment, React REMOUNTS the blocker when the refetch lands and the navigation
  // the user was being asked about is silently dropped — the dialog closes itself.
  if (loading) {
    return (
      <>
        {leaveGuard.dialog}
        <div className="max-w-4xl lg:max-w-6xl xl:max-w-7xl 2xl:max-w-[1600px] mx-auto p-2 md:p-4 lg:p-6">
          <div className="bg-white rounded-lg shadow-md p-6">
            <LoadingSpinner message="Loading order..." />
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        {leaveGuard.dialog}
        <div className="max-w-4xl lg:max-w-6xl xl:max-w-7xl mx-auto p-2 md:p-4 lg:p-6">
          <ErrorMessage error={error} onRetry={fetchOrder} title="Failed to load order" />
          <button
            onClick={() => navigate(-1)}
            className={`${BTN.secondary} mt-4`}
          >
            Go Back
          </button>
        </div>
      </>
    );
  }

  if (!order) return null;

  // Derived values
  // ⚖️ GOODS ONLY (owner ruling 2026-09-19). `totalItems` here is the
  // DELIVERY denominator — it feeds the received pair below and nothing else —
  // so an adjustment row belongs in neither half of it.
  const { received: receivedItems, ordered: orderedItems, total: totalItems } = countDelivery(order.items);
  // The client DELIVERY_STAGE_STATUSES (constants/statuses.js) — the 4-status
  // set incl. `approved` that tracks the received-endpoint gates. This page
  // carried an inline copy of the same four statuses until 2026-08-15.
  const canTrackDelivery = DELIVERY_STAGE_STATUSES.includes(order.status);
  // Order owner identity (integer match) — used to let the person physically taking
  // delivery tick items off themselves. Server gates the matching PATCH the same way.
  const isOwner = !!(user && Number.isInteger(user.id) && Number.isInteger(order.user_id) && order.user_id === user.id);
  // Received-tick plumbing for OrderForm's shared `received` prop — the SAME
  // gate this page's old bespoke checkbox used (admin, order owner, or
  // Stores), now paired with the shared `RECEIVE_ENABLED_STATUSES` lock so a
  // tick greys out on `approved` instead of 400ing when sent. Mirrors how
  // DepartmentOrders' `OrderExpandedRow` resolves `canReceiveItems` /
  // `canReceiveNow`, on the DATA, never re-derived below this point.
  // Stores is per SEAT (2026-09-23): asked of the ORDER's department — the
  // server's own question for the received PATCH — never the active one.
  // 🔀 LR (2026-09-24): the page-local `isAdmin` (site admin OR the unscoped
  // buyer flag) is DELETED. Every control it gated drives a route that asks
  // `canManageDepartmentOrders` in the ORDER's department (PATCH /:id/status,
  // PATCH /:id/expected-delivery, DELETE /:id/files/:fileId, the received
  // PATCH), so each now asks `canManageOrder(order)` — which carries the site
  // admin on its own short-circuit. BOTH DIRECTIONS: it WIDENS to a department
  // buyer/supervisor with no unscoped row (the server already accepted them)
  // and NARROWS away an unscoped buyer outside this order's department (the
  // server already refused them). Pinned by OrderPage.legacyBuyerFlag.test.js.
  const canManageThisOrder = canManageOrder(order);
  const canReceiveItems = canManageThisOrder || isOwner || capabilitiesFor(order.department_id).canManageStores === true;
  // 2026-10-01 — removing the form's ONLY line in edit mode opens THIS page's
  // cancel form (the one the status override's `cancelled` option opens): same
  // route, reason box, audit. A manager gets the status override's gate; the
  // requester gets My Orders' requester arm (`requesterMayCancelOrder`) — the
  // same rule the My Orders Cancel item offers, within what
  // `PATCH /api/orders/:id/cancel` admits for a non-manager.
  const requestCancelFromEdit = mode === 'edit'
    && (canManageThisOrder ? managerMayCancelOrder(order) : (isOwner && requesterMayCancelOrder(order)))
    ? () => {
      setCancelError('');
      setCancellationReason('');
      setShowCancelForm(true);
      // The form sits above the order form; bring it into view.
      window.requestAnimationFrame?.(() => {
        document.querySelector('[data-testid="cancel-form"]')?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      });
    }
    : null;
  // 🔀 **THE INTERNAL-NOTES GATE, WIDENED 2026-09-13 FROM THIS PAGE'S OWN
  // `isAdmin` TO THE SHARED PER-ORDER ONE.** That `isAdmin` (DELETED 2026-09-24,
  // see `canManageThisOrder` above) was site admin OR the UNSCOPED `user_roles`
  // buyer flag — which is the wrong question twice over: it admits a buyer in
  // a department this order does not belong to, and it REFUSES a correctly
  // created department buyer or supervisor who holds
  // `canManageDepartmentOrders` in the order's own department and whom the
  // server accepts. `POST /api/orders/:id/notes` is gated on exactly that
  // capability, so Department Orders' `canManageOrder(detail)` was already the
  // matching client gate and this page disagreed with it — the same fix, and
  // the same argument, as the PO editor a few hundred lines above.
  //
  // 🚨 **IT IS NOT A PURE NARROWING, AND BOTH DIRECTIONS MATTER** — the
  // `req.isBuyer` asymmetry the root `CLAUDE.md` records. It WIDENS to the
  // correctly-created department buyer or supervisor who holds no unscoped
  // `user_roles` row, and it NARROWS away the unscoped buyer standing outside
  // this order's department, whom `POST /api/orders/:id/notes` refuses anyway.
  // Exactly the migration the re-hire button on this page already made; its
  // four cases are the model (`OrderPage.rehireCapability.test.js`).
  //
  // 🔑 **THERE IS NO `isAdmin ||` DISJUNCT HERE AND THERE MUST NOT BE.** A
  // SITE admin holds no assignment rows, but `useOrderAuthority` already
  // carries them on its own `isAdmin` short-circuit. The page-local `isAdmin`
  // that included the unscoped buyer flag is gone; never re-add it.
  //
  // 🔀 SCOPE (2026-09-13): the expanded-row surfaces put Internal notes and
  // Expected Delivery INSIDE Order Details via OrderForm's slots. This page
  // deliberately does NOT — its notes and delivery cards save through their own
  // endpoints (POST /notes, PATCH /expected-delivery), independent of the
  // form's Save, and /orders/:id is slated for retirement. Same rules, own cards.
  const canEditInternalNotes = canManageOrder(order);
  const canReceiveNow = RECEIVE_ENABLED_STATUSES.includes(order.status);
  // `receivingStale` (W1 close-out, above) is a SECOND, independent reason to
  // lock — checked first because it says something is now UNKNOWN about this
  // specific order, which is a stronger claim than "not ordered yet" and
  // deserves its own copy rather than being silently folded into the status
  // tooltip.
  const receivedProp = canTrackDelivery && canReceiveItems ? {
    items: lineStepMap(order.items),
    // 🔀 PER-LINE "ORDERED" (2026-10-28): the same split as the list pages —
    // ordering a line needs order management (`requireOrderManagement`),
    // which is `canManageThisOrder`; Stores and the requester only receive.
    orderedCount: orderedItems,
    canOrder: canManageThisOrder,
    orderLockedReason: RECEIVING_LOCKED_TOOLTIP,
    onToggleOrdered: (itemId, current) => receivedHandlers.toggleOrdered(order.id, itemId, current),
    onOrderAll: (next) => receivedHandlers.orderAll(order.id, next),
    receivedCount: receivedItems,
    total: totalItems,
    busy: receivingBusy,
    locked: receivingStale || !canReceiveNow,
    lockedReason: receivingStale
      ? 'This order\'s received status could not be confirmed after the last change — reload the page before marking more items.'
      : RECEIVING_LOCKED_TOOLTIP,
    onToggleItem: (itemId, current) => receivedHandlers.toggleItem(order.id, itemId, current),
    onReceiveAll: (next) => receivedHandlers.receiveAll(order.id, next),
  } : null;
  const subtotal = (order.items || []).reduce((sum, item) => sum + (parseFloat(item.line_total) || 0), 0);
  // 🔻 `vat` / `grandTotal` deleted 2026-08-08 with the VAT display — see the
  // matching note in `components/OrderForm.js`.
  const hasNoAssignments = !contextIsAdmin && assignments && assignments.length === 0;
  const userAssignedProductions = contextIsAdmin ? null : (hasNoAssignments ? [] : null);
  const originalStatus = order.status || '';
  const submitLabel = originalStatus === 'rejected'
    ? 'Resubmit Order'
    : POST_APPROVAL_STATUSES.includes(originalStatus)
      ? 'Save Changes'
      : 'Save Changes';

  // Partition `order.files` for OrderForm. Order-level = no order_item_id and not
  // delivery-paperwork (that has its own admin uploader). Per-item = keyed by the
  // item's OWN id (2026-09-15; it was keyed by index, which an add/remove in edit
  // mode shifted onto the wrong line).
  const orderFilesList = order.files || [];
  const existingOrderFiles = orderFilesList.filter(f => !f.order_item_id && f.category !== 'delivery');
  // Server-side, DELETE /files/:fileId is gated on canEditOrder. Mirror that
  // client-side so we don't render Remove on files the user can't actually delete.
  const canRemoveFiles = !!order.can_edit;
  // Item files by the item's own id. VIEW mode uploads a pick straight onto that
  // id; EDIT mode passes no `onUpload`, so OrderForm sends the pick to `pending`.
  const itemFilesById = {
    filesByItemId: groupItemAttachmentsByItemId(orderFilesList),
    canRemoveFile: () => true, // the list-level gate is `onExistingFileToggleRemove` below
    ...(mode === 'view' ? { onUpload: uploadItemFilesNow, uploadingItemIds: NO_ITEM_UPLOADS } : {}),
  };

  return (
    <>
      {leaveGuard.dialog}
      <div className="max-w-4xl lg:max-w-6xl xl:max-w-7xl 2xl:max-w-[1600px] mx-auto p-2 md:p-4 lg:p-6 pb-20 lg:pb-16">
        {/* MOBILE header — Back top-left, icon actions top-right */}
        <div className="md:hidden flex items-center justify-between mb-3">
          <button
            onClick={() => mode === 'edit' ? handleCancelEdit() : navigate(-1)}
            className="min-h-[44px] px-3 bg-[#1e3a5f] text-white rounded-lg hover:bg-[#162d4a] font-medium text-sm transition inline-flex items-center gap-1"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <div className="flex items-center gap-2">
            {/* Download / Print — one budget-style dropdown (Download PDF / Print… / Share…) */}
            <OrderPdfButton order={order} supplierContact={supplierContact} />
          </div>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100" data-testid="order-ref">
              {mode === 'edit' ? 'Edit ' : ''}Order {getOrderTitle(order, { isAdmin: contextIsAdmin })}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Submitted on {formatReadableDate(order.order_date || order.created_at) || 'Unknown'}
              <span className="md:hidden"> &mdash; <span
                className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800'}`}
              >
                {STATUS_LABELS[order.status] || (order.status || 'submitted').charAt(0).toUpperCase() + (order.status || 'submitted').slice(1)}
              </span></span>
            </p>

            {/* MOBILE-only Edit CTA below title */}
            {mode === 'view' && order.can_edit && (
              <button
                onClick={() => setMode('edit')}
                className="md:hidden w-full mt-3 min-h-[44px] bg-amber-500 dark:bg-amber-500/70 text-white rounded-lg hover:bg-amber-600 font-medium text-sm transition inline-flex items-center justify-center gap-2"
                data-testid="edit-button-mobile"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {order.status === 'rejected' ? 'Edit & Resubmit' : 'Edit Order'}
              </button>
            )}
            {/* 🔴 THE MOBILE "Restore Order" BUTTON WAS HERE AND IS DELETED
                (2026-08-12), together with its byte-identical desktop twin in the
                action row below and the `PATCH /:id/uncancel` route both called.
                Do not re-add either — and note there were always TWO, so a
                reinstatement that only puts one back is worse than none.
                Full reasoning at the desktop tombstone below. */}
          </div>
          {/* DESKTOP action row */}
          <div className="hidden md:flex flex-wrap items-center justify-start gap-3">
            {/* Inline edit-mode notices (sit to the left of the Save button so they don't push the page down) */}
            {mode === 'edit' && originalStatus === 'rejected' && (
              <span className="text-xs px-2 py-1 bg-amber-50 border border-amber-300 rounded text-amber-800 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-300" data-testid="resubmit-notice-inline">
                <strong>Rejected</strong> — click Resubmit to send for approval
              </span>
            )}
            {mode === 'edit' && POST_APPROVAL_STATUSES.includes(originalStatus) && (
              <span className="text-xs px-2 py-1 bg-blue-50 border border-blue-300 rounded text-blue-800 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300">
                Order is <strong>{POST_APPROVAL_LABELS[originalStatus] || originalStatus}</strong> — changes won't affect the status
              </span>
            )}
            {submitSuccess && (
              <span className="text-xs px-2 py-1 bg-green-50 border border-green-300 rounded text-green-800 dark:bg-green-900/20 dark:border-green-700 dark:text-green-300 inline-flex items-center gap-1" data-testid="edit-success-inline">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                {originalStatus === 'rejected' ? 'Resubmitted' : 'Saved'}
              </span>
            )}
            {mode === 'view' && order.can_edit && (
              <button
                onClick={() => setMode('edit')}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 font-medium text-sm transition flex items-center gap-2"
                data-testid="edit-button"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                {order.status === 'rejected' ? 'Edit & Resubmit' : 'Edit'}
              </button>
            )}
            {mode === 'edit' && (
              <button
                type="button"
                onClick={handleCancelEdit}
                disabled={submitting}
                className="px-4 py-2 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/50 border border-red-200 dark:border-red-800 disabled:opacity-50 rounded-lg font-medium text-sm transition flex items-center gap-2"
                aria-label="Discard unsaved changes"
                data-testid="discard-changes-button-top"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
                Discard Changes
              </button>
            )}
            {mode === 'edit' && (
              <button
                type="submit"
                form="order-form"
                disabled={submitting}
                className={BTN.confirm}
                data-testid="edit-button-save"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                {submitting ? 'Saving…' : (order.status === 'rejected' ? 'Resubmit Order' : 'Save Changes')}
              </button>
            )}
            {/* 🔴 THE DESKTOP "Restore Order" BUTTON WAS HERE AND IS DELETED
                (2026-08-12). DO NOT RE-ADD IT, gated or otherwise.

                Product owner: *"the restore is not obvious to what it does"*.
                Leaving `cancelled` is the status dropdown's job — it already
                moved an order out of `cancelled` and now also clears
                `cancellation_reason`, nulls `status_before_cancel`, writes both
                audit entries and notifies the requester. See the "Restore IS
                DELETED" entry in `client/src/pages/CLAUDE.md`. Accepted cost,
                stated explicitly: nothing can be un-cancelled by anyone, and a
                mistaken cancel means raising a new order.

                🔑 DELETING THIS CLOSED A NAME-AS-IDENTITY FINDING OUTRIGHT.
                Its gate was `isAdmin || order.requested_by === localStorage
                'sfx_requested_by'` — a DISPLAY-NAME match, while `isOwner`
                (`order.user_id === user.id`) was computed in this same
                component and not used. A namesake saw the button; the real
                owner lost it after a rename. This and its mobile twin were the
                LAST TWO reads of `sfx_requested_by` in this file, so the
                READ-SCOPE row for `OrderPage.js` in `docs/ROLES.md` §I is
                RETIRED, not merely re-cited. Never reintroduce a control here
                that resolves the viewer by name — `user.id` is in scope.

                🪤 There were TWO copies of this control, mobile and desktop,
                byte-identical in behaviour. Anything reinstated here has a twin
                above, and one without the other is a breakpoint-dependent
                authority surface. */}
            {/* Download / Print — one budget-style dropdown (Download PDF / Print… / Share…) */}
            <OrderPdfButton order={order} supplierContact={supplierContact} />
            {/* Status override dropdown - buyer/admin only, view mode */}
            {canManageThisOrder && mode === 'view' && (
              <select
                value=""
                onChange={async (e) => {
                  const newStatus = e.target.value;
                  if (!newStatus) return;
                  e.target.value = '';
                  if (newStatus === 'cancelled') {
                    setShowCancelForm(true);
                    return;
                  }
                  const currentLabel = (order.status || '').replace(/_/g, ' ');
                  const newLabel = newStatus.replace(/_/g, ' ');
                  // Same warning as `handleMarkDelivered` above and as
                  // `InlineStatusJump`, from the one shared sentence — this
                  // `<select>` reaches `delivered` too.
                  const deliveredNotice = newStatus === 'delivered'
                    ? `\n\n${deliveredTickNotice(order)}`
                    : '';
                  if (!window.confirm(`Change order status from "${currentLabel}" to "${newLabel}"?${deliveredNotice}`)) return;
                  try {
                    const body = { status: newStatus };
                    if (newStatus === 'rejected') {
                      const reason = window.prompt('Enter rejection reason:');
                      if (!reason) return;
                      body.rejection_reason = reason;
                    }
                    const res = await apiFetch(`${API_BASE}/api/orders/${id}/status`, {
                      method: 'PATCH',
                      body: JSON.stringify(body),
                    });
                    if (!res.ok) {
                      const data = await res.json();
                      throw new Error(data.error || 'Failed to update status');
                    }
                    fetchOrder();
                  } catch (err) {
                    alert(err.message);
                  }
                }}
                className="w-full md:w-auto min-h-[44px] md:min-h-0 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                data-testid="status-override-select"
              >
                <option value="">Change status...</option>
                {['submitted', 'approved', 'rejected', 'ordered', 'partially_delivered', 'delivered', 'cancelled']
                  .filter(s => s !== order.status)
                  .map(s => (
                    <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                  ))
                }
              </select>
            )}
            <span
              className={`hidden md:inline-flex px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-800'}`}
              data-testid="order-status"
            >
              {STATUS_LABELS[order.status] || (order.status || 'submitted').charAt(0).toUpperCase() + (order.status || 'submitted').slice(1)}
            </span>
          </div>
        </div>

        {/* Desktop back link */}
        <div className="hidden md:flex items-center gap-4 mb-4">
          <button
            onClick={() => mode === 'edit' ? handleCancelEdit() : navigate(-1)}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            {mode === 'edit' ? 'Cancel' : 'Back'}
          </button>
        </div>

        {/* Mobile-only edit-mode banners (desktop versions sit inline in the action row above) */}
        {mode === 'edit' && originalStatus === 'rejected' && (
          <div className="md:hidden mb-4 p-3 bg-amber-50 border border-amber-300 rounded-lg text-amber-800 text-sm dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-300" data-testid="resubmit-notice">
            <p className="font-semibold">This order was rejected.</p>
            <p className="mt-1">Make your changes below and click <strong>Resubmit Order</strong> to send it back for approval.</p>
          </div>
        )}

        {mode === 'edit' && POST_APPROVAL_STATUSES.includes(originalStatus) && (
          <div className="md:hidden mb-4 p-3 bg-blue-50 border border-blue-300 rounded-lg text-blue-800 text-sm dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300">
            <p className="font-semibold">This order has been {POST_APPROVAL_LABELS[originalStatus] || originalStatus}.</p>
            <p className="mt-1">Changes will be saved without affecting the order status.</p>
          </div>
        )}

        {/* Permission-denied banner (user landed on /edit but server says no) */}
        {editPermissionDenied && (
          <div className="mb-4 p-4 bg-red-50 border border-red-300 rounded-lg text-red-800 dark:bg-red-900/20 dark:border-red-700/60 dark:text-red-200" data-testid="edit-permission-denied">
            <p className="font-semibold">You don't have permission to edit this order at its current stage.</p>
            <p className="text-sm mt-1">The order is shown below in read-only view.</p>
          </div>
        )}

        {/* Mobile-only edit-mode success banner (desktop version sits inline in the action row above) */}
        {submitSuccess && (
          <div className="md:hidden mb-6 p-4 bg-green-50 border border-green-300 rounded-lg text-green-800 dark:bg-green-900/20 dark:border-green-700 dark:text-green-300" data-testid="edit-success">
            <p className="font-semibold">
              {originalStatus === 'rejected' ? 'Order resubmitted successfully!' : 'Order updated successfully!'}
            </p>
          </div>
        )}

        {/* Cancel Order Form (admin-triggered from status override OR user-triggered from edit) */}
        {showCancelForm && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4" data-testid="cancel-form">
            <h4 className="text-sm font-semibold text-red-700 mb-2">Cancel Order</h4>
            {(order.status === 'delivered' || order.status === 'ordered' || order.status === 'partially_delivered') && (
              <div className="bg-amber-50 border border-amber-300 rounded-md p-3 mb-3 dark:bg-amber-900/20 dark:border-amber-700">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  Warning: This order is currently marked as <strong>{order.status.replace('_', ' ')}</strong>. Are you sure you want to cancel it?
                </p>
              </div>
            )}
            {cancelError ? (
              <div className="mb-3">
                <p className="text-sm text-red-600 font-medium" data-testid="cancel-error">{cancelError}</p>
                <button
                  onClick={() => { setShowCancelForm(false); setCancelError(''); }}
                  className="mt-2 px-4 py-3 md:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm transition"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <label className="block text-sm text-red-700 mb-1">Cancellation Reason <span className="text-gray-500 dark:text-gray-400">(optional)</span></label>
                <textarea
                  value={cancellationReason}
                  onChange={(e) => setCancellationReason(e.target.value)}
                  placeholder="Enter reason for cancellation..."
                  rows={2}
                  className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 focus:outline-none"
                  data-testid="cancel-reason-input"
                />
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={handleCancelOrder}
                    disabled={actionLoading}
                    className="px-4 py-3 md:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition"
                    data-testid="confirm-cancel-button"
                  >
                    {actionLoading ? 'Cancelling...' : 'Confirm Cancel'}
                  </button>
                  <button
                    onClick={() => { setShowCancelForm(false); setCancellationReason(''); }}
                    className="px-4 py-3 md:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm transition"
                    data-testid="cancel-cancel-button"
                  >
                    Exit
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Rejection banner (visible in both view + edit). 🔀 2026-10-19: the
            one-line "Rejected … — <reason>" shape the list panels use. This
            page loads the DETAIL payload only, which carries no who/when, so
            it reads "Rejected — <reason>"; the rejecting step in Order
            Progress below still names who and when. */}
        {order.status === 'rejected' && order.rejection_reason && (
          <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
            <OrderRejectionLine reason={order.rejection_reason} testId="order-page-rejection" />
          </div>
        )}

        {/* Internal notes banner (visible in both modes). 🔻 Labelled
            "Admin Notes" until 2026-09-13; the field, the column and who may
            read it are unchanged. No ⓘ here — a banner that only appears when
            there IS a note is not a form field, and the printed/not-printed
            contrast is a thing you need while TYPING, which is the editor's
            job below. */}
        {order.admin_notes && (
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
            <p className="text-sm font-semibold text-blue-800 dark:text-blue-300">{INTERNAL_NOTES_LABEL}</p>
            <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">{order.admin_notes}</p>
          </div>
        )}

        {/* ===== Order Form (renders the editable / read-only fields) ===== */}
        {/* Key intentionally excludes `mode` so the form survives the view↔edit
            flip — that preserves per-line collapsed state, attachment-toggle state,
            notes-expanded state etc. The internal re-hydrate useEffect (~L161 in
            OrderForm.js) handles syncing form + items whenever `initialOrder`
            changes, so post-save we still pick up the freshly-fetched values. */}
        <OrderForm
          key={`order-form-${order.id}`}
          mode="edit"
          readOnly={mode === 'view'}
          initialOrder={order}
          productions={productions}
          suppliers={suppliers}
          supplierSuggestions={supplierSuggestions}
          loadFailures={loadFailures}
          gags={gags}
          subDepartments={subDepartments}
          costCodes={costCodes}
          paymentTypes={paymentTypes}
          userAssignedProductions={userAssignedProductions}
          originalStatus={originalStatus}
          submitting={submitting}
          submitError={submitError}
          submitLabel={submitLabel}
          onSubmit={handleSaveFromForm}
          onCancel={mode === 'edit' ? handleCancelEdit : null}
          onDirtyChange={setFormDirty}
          showOrderFileUpload
          showItemFileUpload
          onOrderFilesChange={mode === 'view' ? uploadOrderFilesNow : null}
          itemFilesImmediate={itemFilesById}
          pendingFiles={mode === 'edit' ? pending : null}
          pendingFilesDisabled={submitting}
          existingOrderFiles={existingOrderFiles}
          pendingDeleteFileIds={pendingDeleteFileIds}
          onExistingFileToggleRemove={canRemoveFiles ? handleExistingFileToggleRemove : null}
          initialItemsCollapsed
          received={receivedProp}
          postApprovalGuardStatus={POST_APPROVAL_STATUSES.includes(originalStatus) ? originalStatus : null}
          onRequestCancelOrder={requestCancelFromEdit}
        />

        {/* ===== Supplier contact toggle — view mode only ===== */}
        {mode === 'view' && order.supplier_name && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6 mt-4">
            <button
              onClick={() => setShowSupplierContact(v => !v)}
              className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-sm text-left"
              data-testid="supplier-name-link"
            >
              {showSupplierContact ? 'Hide' : 'Show'} supplier contact details
              <span className="ml-1 text-xs text-gray-400">{showSupplierContact ? '▲' : '▼'}</span>
            </button>
            {showSupplierContact && supplierContact && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700" data-testid="supplier-contact-details">
                <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-3">{order.supplier_name}</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Phone</p>
                    {supplierContact.phone ? (
                      <a href={`tel:${supplierContact.phone}`} className="text-blue-600 hover:underline" data-testid="supplier-phone">{supplierContact.phone}</a>
                    ) : <p className="text-gray-500 dark:text-gray-400">Not set</p>}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Email</p>
                    {supplierContact.email ? (
                      <a href={`mailto:${supplierContact.email}`} className="text-blue-600 hover:underline" data-testid="supplier-email">{supplierContact.email}</a>
                    ) : <p className="text-gray-500 dark:text-gray-400">Not set</p>}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Website</p>
                    {supplierContact.website ? (
                      <a href={supplierContact.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" data-testid="supplier-website">{supplierContact.website}</a>
                    ) : <p className="text-gray-500 dark:text-gray-400">Not set</p>}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Account Number</p>
                    <p className="text-gray-800 dark:text-gray-200" data-testid="supplier-account">{supplierContact.account_number || <span className="text-gray-500 dark:text-gray-400">Not set</span>}</p>
                  </div>
                </div>
              </div>
            )}
            {showSupplierContact && !supplierContact && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-400 italic">No contact details available for this supplier.</p>
              </div>
            )}
          </div>
        )}

        {/* ===== Expected delivery editor (admin, ordered+ statuses) ===== */}
        {mode === 'view' && ['ordered', 'partially_delivered', 'delivered'].includes(order.status) && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6 mt-4" data-testid="expected-delivery-section">
            <h3 className="text-base font-semibold text-gray-700 dark:text-gray-200 mb-2">Expected Delivery</h3>
            {editingExpectedDate && canManageThisOrder ? (
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="date"
                  value={editExpectedDate}
                  onChange={e => setEditExpectedDate(e.target.value)}
                  className="px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  data-testid="edit-expected-date-input"
                />
                <button
                  onClick={handleUpdateExpectedDate}
                  disabled={expectedDateSaving}
                  className={BTN_XS.confirm}
                  data-testid="save-expected-date"
                >
                  {expectedDateSaving ? '...' : 'Save'}
                </button>
                <button
                  onClick={() => setEditingExpectedDate(false)}
                  className="px-2 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                {order.expected_delivery_date ? (
                  (() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const expDate = new Date(order.expected_delivery_date + 'T00:00:00');
                    expDate.setHours(0, 0, 0, 0);
                    // 🔀 CONTRACT CHANGE (2026-08-17) — was `order.status !== 'delivered'`, which
                    // read a rejected or cancelled order as still racing this deadline (this site
                    // has NO outer status gate, unlike MyOrders.js's equivalent paragraph, so it
                    // was reachable: an order ordered/expected-dated then later cancelled or
                    // rejected showed a false OVERDUE badge on /orders/:id). Reusing the shared
                    // predicate means this can never disagree with the dot/pill/urgency badge
                    // again — see `client/src/constants/statuses.js`.
                    const isOverdue = expDate < today && showsTimeCriticalChrome(order);
                    return (
                      <span className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-gray-800 dark:text-gray-200'}`} data-testid="expected-delivery-date">
                        {formatReadableDate(order.expected_delivery_date)}
                        {isOverdue && (
                          <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-semibold" data-testid="overdue-badge">
                            OVERDUE
                          </span>
                        )}
                      </span>
                    );
                  })()
                ) : (
                  <span className="text-sm text-gray-400">Not set</span>
                )}
                {canManageThisOrder && (
                  <button
                    onClick={() => { setEditExpectedDate(order.expected_delivery_date || ''); setEditingExpectedDate(true); }}
                    className="text-blue-600 hover:text-blue-800 text-xs"
                    data-testid="edit-expected-date-btn"
                  >
                    {order.expected_delivery_date ? 'Edit' : 'Set date'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ===== Delivery Progress (always read-and-act, both modes) ===== */}
        {canTrackDelivery && totalItems > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6 mt-4" data-testid="delivery-progress">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Delivery Progress</h3>
              <span className={`text-sm font-medium ${receivedItems === totalItems ? 'text-green-600' : receivedItems > 0 ? 'text-orange-600' : 'text-gray-500'}`} data-testid="delivery-progress-text">
                {receivedItems} of {totalItems} items received
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3" data-testid="delivery-progress-bar">
              <div
                className={`h-3 rounded-full transition-all duration-300 ${receivedItems === totalItems ? 'bg-green-500' : 'bg-orange-500'}`}
                style={{ width: `${totalItems > 0 ? (receivedItems / totalItems) * 100 : 0}%` }}
              ></div>
            </div>
            {receivedItems === totalItems && totalItems > 0 && (
              <p className="text-xs text-green-600 mt-1 font-medium">All items have been received!</p>
            )}

            {/* Per-item received ticks now render inside OrderForm's own line
                items (the shared 2026-08-15 received-tick cluster — see the
                `received` prop passed to <OrderForm> above). The bespoke
                checkbox block that used to render here is DELETED: it predated
                that cluster, never picked up the 2026-08-16 "greyed until
                Ordered" gate, and stayed live-looking on `approved` while the
                server 400'd it. Do not reinstate a second checkbox here. */}

            {/* Delivery paperwork upload — buyers/admins, the order owner, plus
                Stores (goods-in handler attaches delivery notes / POD). Same
                predicate as `canReceiveItems` above, mirroring how
                DepartmentOrders' `canUploadDelivery` is that page's
                `canReceiveItems` reused verbatim. */}
            {canReceiveItems && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700" data-testid="delivery-paperwork-section">
                <div className="flex items-center justify-between mb-2">
                  <label
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg cursor-pointer transition ${
                      deliveryUploading
                        ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                    data-testid="upload-delivery-paperwork-btn"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                    {deliveryUploading ? 'Uploading...' : 'Upload Paperwork'}
                    {/*
                      No `accept` filter — delivery paperwork is a general-purpose
                      attachment surface, filtered server-side by the
                      BLOCKED_UPLOAD_EXT blocklist (server/helpers/uploads.js).
                      A fixed picker list here would re-impose the removed allowlist.
                    */}
                    <input
                      ref={deliveryFileInputRef}
                      type="file"
                      multiple
                      onChange={handleDeliveryFileUpload}
                      disabled={deliveryUploading}
                      className="hidden"
                      data-testid="delivery-file-input"
                    />
                  </label>
                  <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Delivery Paperwork</p>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Upload delivery notes, proof of delivery, or other paperwork</p>
                {order.files && order.files.filter(f => f.category === 'delivery').length > 0 && (
                  <div className="space-y-1.5" data-testid="delivery-files-list">
                    {order.files.filter(f => f.category === 'delivery').map((file) => (
                      <div key={file.id} className="flex items-center justify-between bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg px-3 py-2" data-testid={`delivery-file-${file.id}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-purple-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-sm text-gray-700 dark:text-gray-200 truncate">{file.file_name}</span>
                          {file.file_size && (
                            <span className="text-xs text-gray-400 flex-shrink-0">
                              ({file.file_size < 1024 ? file.file_size + ' B' : file.file_size < 1024 * 1024 ? (file.file_size / 1024).toFixed(1) + ' KB' : (file.file_size / (1024 * 1024)).toFixed(1) + ' MB'})
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                          <a
                            href={file.file_path}
                            download={file.file_name}
                            className="text-purple-600 hover:text-purple-800 text-sm font-medium flex items-center gap-1"
                            data-testid={`download-delivery-file-${file.id}`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                            Download
                          </a>
                          {/* Remove: admin/buyer can remove any file; an owner can
                              only remove files THEY uploaded (server enforces too). */}
                          {(canManageThisOrder || (user && file.uploaded_by_user_id === user.id)) && (
                          <button
                            type="button"
                            onClick={async () => {
                              if (!window.confirm(`Remove "${file.file_name}"? This cannot be undone.`)) return;
                              setActionMessage(null);
                              try {
                                const res = await apiFetch(`${API_BASE}/api/orders/${id}/files/${file.id}`, { method: 'DELETE' });
                                if (!res.ok) {
                                  const err = await res.json();
                                  throw new Error(err.error || 'Failed to remove paperwork');
                                }
                                const refetch = await apiGet(`${API_BASE}/api/orders/${id}`);
                                if (refetch.ok) {
                                  const updated = await refetch.json();
                                  setOrder(updated);
                                }
                                setActionMessage({ type: 'success', text: 'Paperwork removed' });
                              } catch (err) {
                                setActionMessage({ type: 'error', text: err.message });
                              }
                            }}
                            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                            aria-label={`Remove ${file.file_name}`}
                            title="Remove"
                            data-testid={`delete-delivery-file-${file.id}`}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {order.files && order.files.filter(f => f.category === 'delivery').length === 0 && (
                  <p className="text-xs text-gray-400 italic" data-testid="no-delivery-files">No delivery paperwork uploaded yet</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* ===== Per-item Hire Status (visible in both view + edit) =====
            Per-item files now live inside each line item card inside OrderForm. This
            block surfaces hire-only metadata that OrderForm doesn't render — hire
            status pill + the "Bring Back On Hire" action (view-only so editing
            the form can't race a status transition). Off-hired items are surfaced
            here so a buyer can re-hire without leaving the page.

            The button is gated on `useOrderAuthority().canManageOrder(order)`, NOT
            the old page-local `isAdmin` (site admin OR the caller's UNSCOPED
            `user_roles` buyer flag — deleted 2026-09-24). The server route this button drives,
            `PATCH /api/hires/:itemId/re-hire` (`server/routes/hires.js`), gates on
            `requireDepartmentCapability(req, res, item.order_department_id,
            'canManageDepartmentOrders')` — resolved in the HIRE ITEM'S OWN ORDER'S
            department, seeded 1 for admin, buyer AND supervisor alike. `isAdmin`
            both excludes a correctly-seeded department buyer/supervisor with no
            unscoped `user_roles` row, and admits an unscoped-buyer-flagged caller
            for an order in a department they hold no assignment in at all — see
            `client/src/hooks/useOrderAuthority.js`'s docblock for the general
            shape. Pinned both directions by
            `client/src/pages/OrderPage.rehireCapability.test.js`. */}
        {(order.items || []).some(i => i.is_hire && i.hire_status) && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6 mt-4">
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700 pb-1 mb-2">Hire Status</h3>
            <div className="space-y-3">
              {(order.items || []).map((item, index) => {
                if (!(item.is_hire && item.hire_status)) return null;
                return (
                  <div key={item.id || index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-700/30">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Item {index + 1}: {item.part_number && <span className="text-gray-500 dark:text-gray-400 mr-1">{item.part_number} -</span>}
                      {item.description || <span className="italic text-gray-400">No description</span>}
                    </p>
                    <p className="text-xs mb-2">
                      <span className="text-gray-500 dark:text-gray-400">Hire status: </span>
                      <span className={`font-medium ${
                        item.hire_status === 'off_hired' ? 'text-gray-500' :
                        item.hire_status === 'overdue' ? 'text-red-600' :
                        item.hire_status === 'on_hire' ? 'text-blue-600' : 'text-amber-600'
                      }`}>
                        {item.hire_status === 'off_hired' ? 'Off-Hired' :
                         item.hire_status === 'on_hire' ? 'On Hire' :
                         item.hire_status === 'overdue' ? 'Overdue' :
                         item.hire_status === 'pending' ? 'Upcoming' : item.hire_status}
                      </span>
                      {item.actual_return_date && (
                        <span className="ml-2 text-gray-500 dark:text-gray-400">Returned: {formatReadableDate(item.actual_return_date)}</span>
                      )}
                    </p>
                    {mode === 'view' && canManageOrder(order) && item.hire_status === 'off_hired' && (
                      <button
                        onClick={async () => {
                          const hireTo = item.hire_to;
                          const today = toLocalISO(new Date());
                          if (hireTo && hireTo < today) {
                            if (!window.confirm(
                              `The hire end date (${formatReadableDate(hireTo)}) has passed.\n\nDon't forget to update the hire dates with the new dates after bringing this item back on hire.\n\nContinue?`
                            )) return;
                          }
                          try {
                            const res = await apiPatch(`${API_BASE}/api/hires/${item.id}/re-hire`, {});
                            if (!res.ok) {
                              const err = await res.json();
                              throw new Error(err.error || 'Failed to bring back on hire');
                            }
                            notifyDataBus('hires-changed', { departmentId: order?.department_id, reason: 're-hire' });
                            fetchOrder();
                          } catch (err) {
                            alert(err.message);
                          }
                        }}
                        className="text-xs px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded font-medium transition-colors inline-flex items-center gap-1"
                        data-testid={`rehire-btn-order-${item.id}`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Bring Back On Hire
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Order-level Attachments are now rendered inside OrderForm's "Notes, Totals
            & Attachments" section (existing files + pending uploads + add/remove controls in one
            place). The delivery-paperwork uploader below is a separate admin flow. */}


        {/* ===== Approval workflow / order lifecycle stepper ===== */}
        <div className="mt-4">
          <ApprovalStatus
            orderId={order.id}
            orderStatus={order.status}
            items={order.items}
            statusBeforeCancel={order.status_before_cancel}
          />
        </div>

        {/* ===== Admin Actions (always action-able, admin only) ===== */}
        {/* 🔑 Everyone who can CAUSE a message: since 2026-09-24 the status
            buttons and the notes editor share ONE gate, `canManageOrder(order)`
            (the unscoped-buyer `isAdmin` that made them two sets is deleted). */}
        {canManageThisOrder && actionMessage && (
          <div className={`rounded-lg p-4 mt-4 ${actionMessage.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-700 dark:text-green-300' : 'bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-700 dark:text-red-300'}`} data-testid="action-message">
            <p className="text-sm font-medium">{actionMessage.text}</p>
          </div>
        )}
        {/* ===== Internal notes — ITS OWN CARD SINCE 2026-09-13 =============
            It sat INSIDE the `Admin Actions` card, sharing that card's
            `isAdmin` gate with the status-transition buttons. Widening the
            notes gate to `canEditInternalNotes` (see its definition) could not
            be done in place without handing a department manager the status
            buttons too, which is a product change nobody asked for — so the
            field moved OUT rather than the card's gate moving. 🔀 2026-09-24
            (LR, owner ruling (c)): the card's gate DID move after all — to
            `canManageOrder(order)`, the capability every status route asks.
            🪤 `data-testid="admin-notes-section"` is DELIBERATELY UNRENAMED
            (grepped first: it has no consumer in `client/src` or `e2e/`, but a
            testid has consumers a call graph does not show and renaming one is
            the only move that CAN break a future selector). Same for
            `admin-notes-input` and `save-admin-notes-button`. */}
        {canEditInternalNotes && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6 mt-4" data-testid="admin-notes-section">
            <label htmlFor="internal-notes" className={`${FIELD_LABEL_CLASS} flex items-center gap-1`}>
              {INTERNAL_NOTES_LABEL}
              <InfoTip title={INTERNAL_NOTES_TOOLTIP} />
            </label>
            <textarea
              id="internal-notes"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder={INTERNAL_NOTES_PLACEHOLDER}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
              data-testid="admin-notes-input"
            />
            <button
              onClick={handleSaveAdminNotes}
              disabled={notesSaving}
              className={`${BTN.confirm} mt-2 min-h-[44px] md:min-h-0 justify-center`}
              data-testid="save-admin-notes-button"
            >
              {notesSaving ? 'Saving...' : 'Save Notes'}
            </button>
          </div>
        )}
        {canManageThisOrder && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6 mt-4" data-testid="admin-actions">
            <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 border-b border-gray-200 dark:border-gray-700 pb-1 mb-2">Admin Actions</h3>

            {/* Status action buttons — view mode only. While editing, the form's
                Save/Cancel buttons own the action surface; status transitions would
                race against unsaved form changes. */}
            {mode === 'view' && (
            <>
            <div className="flex flex-wrap gap-3">
              {(order.status === 'submitted' || order.status === 'rejected') && (
                <button
                  onClick={handleApprove}
                  disabled={actionLoading}
                  className="px-4 py-3 md:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition"
                  data-testid="approve-button"
                >
                  {actionLoading ? 'Processing...' : 'Approve Order'}
                </button>
              )}
              {order.status === 'submitted' && !showRejectForm && (
                <button
                  onClick={() => setShowRejectForm(true)}
                  disabled={actionLoading}
                  className="px-4 py-3 md:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition"
                  data-testid="reject-button"
                >
                  Reject Order
                </button>
              )}
              {order.status === 'rejected' && (
                <span className="px-4 py-3 md:py-2 bg-red-100 text-red-800 rounded-lg font-medium text-sm" data-testid="rejected-badge">
                  This order has been rejected
                </span>
              )}
              {order.status === 'approved' && !showOrderedForm && (
                <button
                  onClick={() => setShowOrderedForm(true)}
                  disabled={actionLoading}
                  className="px-4 py-3 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition"
                  data-testid="mark-ordered-button"
                >
                  Mark as Ordered
                </button>
              )}
              {(order.status === 'ordered' || order.status === 'partially_delivered') && (
                <button
                  onClick={handleMarkDelivered}
                  disabled={actionLoading}
                  className="px-4 py-3 md:py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition"
                  data-testid="mark-delivered-button"
                >
                  {actionLoading ? 'Processing...' : 'Mark as Delivered'}
                </button>
              )}
              {order.status === 'partially_delivered' && (
                <span className="px-4 py-3 md:py-2 bg-orange-100 text-orange-800 rounded-lg font-medium text-sm" data-testid="partially-delivered-badge">
                  {/* 🔻 'Partially Delivered' → 'Partial delivery' (owner,
                      2026-09-13). Another hand-written copy of
                      `STATUS_LABELS.partially_delivered`; declared here so the
                      next wording change knows to look. */}
                  Partial delivery ({receivedItems}/{totalItems} items)
                </span>
              )}
              {order.status === 'delivered' && (
                <span className="px-4 py-3 md:py-2 bg-purple-100 text-purple-800 rounded-lg font-medium text-sm" data-testid="delivered-badge">
                  This order has been delivered
                </span>
              )}
            </div>

            {/* Reject form */}
            {showRejectForm && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700" data-testid="reject-form">
                <label className="block text-sm font-medium text-red-700 mb-1">
                  Rejection Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => { setRejectionReason(e.target.value); setRejectError(''); }}
                  placeholder="Enter the reason for rejecting this order..."
                  rows={3}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500 focus:outline-none ${rejectError ? 'border-red-500 bg-red-50' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                  data-testid="rejection-reason-input"
                />
                {rejectError && (
                  <p className="text-sm text-red-600 mt-1" data-testid="reject-error">{rejectError}</p>
                )}
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={handleReject}
                    disabled={actionLoading}
                    className="px-4 py-3 md:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition"
                    data-testid="confirm-reject-button"
                  >
                    {actionLoading ? 'Processing...' : 'Confirm Rejection'}
                  </button>
                  <button
                    onClick={() => { setShowRejectForm(false); setRejectionReason(''); setRejectError(''); }}
                    className="px-4 py-3 md:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm transition"
                    data-testid="cancel-reject-button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Mark-as-ordered form */}
            {showOrderedForm && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700" data-testid="ordered-form">
                <p className="text-sm font-medium text-blue-700 dark:text-blue-400 mb-2">Mark this order as placed with the supplier</p>
                <label className={FIELD_LABEL_CLASS}>
                  Expected Delivery Date <span className="text-gray-500 dark:text-gray-400">(optional)</span>
                </label>
                <input
                  type="date"
                  value={expectedDeliveryDate}
                  onChange={e => setExpectedDeliveryDate(e.target.value)}
                  className="w-full sm:w-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  data-testid="expected-delivery-input"
                />
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleMarkOrdered}
                    disabled={actionLoading}
                    className="px-4 py-3 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition"
                    data-testid="confirm-ordered-button"
                  >
                    {actionLoading ? 'Processing...' : 'Confirm Ordered'}
                  </button>
                  <button
                    onClick={() => { setShowOrderedForm(false); setExpectedDeliveryDate(''); }}
                    className="px-4 py-3 md:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm transition"
                    data-testid="cancel-ordered-button"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            </>
            )}
          </div>
        )}

        {/* ===== Activity History =====
            EXTRACTED to `components/OrderActivityHistory.js` on 2026-08-10, when
            the expanded order row on Department Orders / To Approve needed the
            same section. The action colour and label maps went with it: two copies
            of those is how a newly-added audit action comes to render as a grey dot
            and a raw enum on one surface and correctly on the other. Same markup,
            same testids (audit-trail-section / audit-trail-toggle / audit-entry),
            same sfx_order_activity_history key. TWO differences (2026-09-12):
            defaultOpen is TRUE here and FALSE in the row, and only THIS surface
            reads or writes the key -- the row passes persistOpenState=false, so
            the row always opens collapsed. A page devoted to one order has the height
            to spend and the history is a reason to be here; a table row does not,
            and this list is unbounded. Do not unify the default; see the key's
            docblock in that component.
            The component fetches lazily on first open, so a user who has collapsed
            the section no longer pays for a request this page used to issue on
            every mount. refreshSignal is bumped after a successful edit. */}
        <OrderActivityHistory
          orderId={id}
          defaultOpen={true}
          refreshSignal={auditRefreshSignal}
        />

        {/* ===== Totals — fixed slim bar always pinned at the viewport bottom.
            sticky-bottom only pins when the element WOULD scroll below viewport, so
            it's the wrong primitive for an always-visible bar. fixed handles it,
            with `left` offset by the --sidebar-width CSS var that Layout.js:1088
            sets on the root (4rem collapsed / 16rem expanded on desktop, unset on
            mobile so it falls back to 0). Bottom offset is 16 (64px) on mobile to
            clear the bottom nav, 0 on desktop. `no-print` ancestor keeps it off the
            print layout. ⚠️ The old note about "the Subtotal / VAT breakdown
            collapses on narrow screens so the bar stays one line" expired on
            2026-08-08: there is no breakdown any more, just one Total, so the bar
            is a single line at every width and nothing needs to collapse. */}
        <div className="fixed bottom-16 lg:bottom-0 left-0 lg:left-[var(--sidebar-width)] right-0 z-30 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm border-t border-gray-200 dark:border-gray-700 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_-4px_12px_rgba(0,0,0,0.3)] no-print" data-testid="order-totals-sticky">
          <div className="max-w-4xl lg:max-w-6xl xl:max-w-7xl 2xl:max-w-[1600px] mx-auto flex items-center justify-end gap-4 md:gap-6 px-3 md:px-6 py-2 text-sm">
            {/* 🔻 ONE TOTAL SINCE 2026-08-08 — the Subtotal / VAT / Grand Total
                breakdown went with the rest of the VAT display, and the label is
                a bare "Total" because the app can no longer say whether the
                stored figures are ex- or inc-VAT. See `hooks/useVatMode.js`. */}
            <span className="text-base font-bold text-gray-800 dark:text-white">
              Total: {formatCurrency(subtotal)}
            </span>
          </div>
        </div>
      </div>

      {/* ===== Post-approval edit pause — THE shared dialog (2026-09-13) ===== */}
      <PostApprovalEditDialog
        open={showEditWarning}
        status={originalStatus}
        action="save"
        busy={submitting}
        onConfirm={confirmPostApprovalEdit}
        onCancel={() => setPendingPayload(null)}
      />
    </>
  );
}

// One instance per ORDER and MODE — `orders/:id` and `orders/:id/edit` render this
// at the same tree position, so without the key an edit's state and pending files
// survived into another order (R14 review). See `utils/remountOnRouteParams.js`.
export default remountOnRouteParams(OrderPage, (params, props) => `${params.id}:${props.initialMode || 'view'}`);
